// src/app/components/login/force-set-password-page.tsx
//
// Forced first-login password set. Shown after a user authenticates on a
// temporary / admin-set credential (must_change_password = true) and before
// they can reach the app. Calls /auth/complete-initial-password, which clears
// the flag and keeps the current session alive — no second login.
import { useState } from "react";
import { Eye, EyeOff, Lock, ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import { authApi } from "@/api/auth";
import { PasswordRequirements, checkPassword } from "./password-requirements";

interface ForceSetPasswordPageProps {
  /** The signed-in user's email/username, shown for context. */
  username: string;
  /** Fired once the password is set and the flag cleared — enter the app. */
  onDone: () => void;
  /** "This isn't me" escape hatch — drop back to the login screen. */
  onLogout: () => void;
}

export function ForceSetPasswordPage({
  username,
  onDone,
  onLogout,
}: ForceSetPasswordPageProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirm,     setConfirm]     = useState("");
  const [showNew,     setShowNew]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState<string | null>(null);

  const checks   = checkPassword(newPassword);
  const mismatch = confirm.length > 0 && newPassword !== confirm;
  const canSubmit = !saving && checks.allValid && confirm.length > 0 && !mismatch;

  const handleSubmit = async () => {
    setError(null);
    if (!checks.allValid) {
      setError("Your new password doesn't meet the requirements below.");
      return;
    }
    if (mismatch) {
      setError("The two passwords don't match.");
      return;
    }
    setSaving(true);
    try {
      await authApi.completeInitialPassword(newPassword);
      onDone();
    } catch (e: any) {
      setError(e?.message || "We couldn't set your password. Please try again.");
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 44px 12px 40px",
    backgroundColor: "var(--input-background)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius)",
    fontSize: "var(--text-base)",
    color: "var(--foreground)",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        backgroundColor: "var(--muted-30)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          backgroundColor: "var(--background)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "var(--elevation-sm)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "22px 24px",
            borderBottom: "1px solid var(--border)",
            backgroundColor: "var(--muted-30)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ShieldCheck style={{ width: 20, height: 20, color: "var(--accent)" }} />
            <h2 style={{ fontSize: "var(--text-xl)", margin: 0 }}>Set your password</h2>
          </div>
          <p
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--muted-foreground)",
              marginTop: 6,
            }}
          >
            Before you continue, choose a password of your own. You're signed in
            as <strong style={{ color: "var(--foreground)" }}>{username}</strong>.
          </p>
        </div>

        <div style={{ padding: 24 }}>
          {/* New password */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
              New password
            </label>
            <div style={{ position: "relative" }}>
              <Lock
                style={{
                  position: "absolute", left: 14, top: "50%",
                  transform: "translateY(-50%)", width: 16, height: 16,
                  color: "var(--muted-foreground)", pointerEvents: "none",
                }}
              />
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
                placeholder="Enter a new password"
                disabled={saving}
                autoComplete="new-password"
                style={{
                  ...inputStyle,
                  border: `1px solid ${
                    newPassword && !checks.allValid
                      ? "var(--infrared-coral)"
                      : "var(--border)"
                  }`,
                }}
              />
              <button
                type="button"
                onClick={() => setShowNew((s) => !s)}
                disabled={saving}
                aria-label={showNew ? "Hide password" : "Show password"}
                style={{
                  position: "absolute", right: 12, top: "50%",
                  transform: "translateY(-50%)", background: "none",
                  border: "none", cursor: "pointer", padding: 4,
                  color: "var(--muted-foreground)", display: "flex", alignItems: "center",
                }}
              >
                {showNew
                  ? <EyeOff style={{ width: 16, height: 16 }} />
                  : <Eye style={{ width: 16, height: 16 }} />}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
              Confirm new password
            </label>
            <div style={{ position: "relative" }}>
              <Lock
                style={{
                  position: "absolute", left: 14, top: "50%",
                  transform: "translateY(-50%)", width: 16, height: 16,
                  color: "var(--muted-foreground)", pointerEvents: "none",
                }}
              />
              <input
                type={showNew ? "text" : "password"}
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setError(null); }}
                placeholder="Re-enter your new password"
                disabled={saving}
                autoComplete="new-password"
                style={{
                  ...inputStyle,
                  border: `1px solid ${mismatch ? "var(--infrared-coral)" : "var(--border)"}`,
                }}
              />
            </div>
          </div>

          {/* Requirements checklist */}
          {newPassword && (
            <div style={{ marginBottom: 16 }}>
              <PasswordRequirements password={newPassword} />
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 14, padding: "12px 14px", borderRadius: 8,
                border: "1px solid var(--infrared-coral)",
                backgroundColor: "var(--infrared-coral-5)",
                display: "flex", gap: 10, alignItems: "flex-start",
              }}
            >
              <AlertCircle
                style={{ width: 18, height: 18, flexShrink: 0, marginTop: 1, color: "var(--infrared-coral)" }}
              />
              <div style={{ fontSize: "var(--text-sm)", color: "var(--infrared-coral)", lineHeight: 1.5 }}>
                {error}
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              width: "100%", height: 44, padding: "0 18px",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
              backgroundColor: canSubmit ? "var(--accent)" : "var(--muted)",
              color: canSubmit ? "var(--accent-foreground)" : "var(--muted-foreground)",
              border: "none", borderRadius: "var(--radius)",
              cursor: canSubmit ? "pointer" : "not-allowed",
              fontWeight: "var(--font-weight-semibold)",
              opacity: saving ? 0.85 : 1,
            }}
          >
            {saving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <ShieldCheck className="w-4 h-4" />}
            Set password & continue
          </button>

          {/* Escape hatch */}
          <button
            type="button"
            onClick={onLogout}
            disabled={saving}
            style={{
              width: "100%", marginTop: 12, background: "none", border: "none",
              cursor: saving ? "not-allowed" : "pointer",
              fontSize: "var(--text-sm)", color: "var(--muted-foreground)",
            }}
          >
            This isn't me — sign out
          </button>
        </div>
      </div>
    </div>
  );
}
